import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { AccessItem } from "../Models/Models";
import { RootState } from "../Services/Store";
import { mediaUrl } from "../Services/mediaUrl";
import WebService from "../Services/WebService";
import { cancelExpense, fetchExpense, fetchExpenseMeta } from "../Services/expenses";
import AddExpensePanel from "./AddExpensePanel";
import styles from "./ExpenseDetail.module.css";

interface DocumentRow {
  document_id: number;
  file_name?: string;
  original_name?: string;
}

interface LedgerRow {
  id: number;
  account_name?: string;
  related_type?: string;
  entry_type?: string;
  amount?: number | string;
  description?: string;
  entry_date?: string;
}

interface ExpenseDetailData {
  expense_id: number;
  expense_code?: string;
  expense_date?: string;
  category?: string;
  category_label?: string;
  description?: string;
  amount?: number | string;
  payment_method?: string;
  payment_method_label?: string;
  paid_from?: string;
  paid_from_label?: string;
  cash_bank_account_id?: number;
  cash_bank_account_name?: string;
  vendor_id?: number;
  vendor_payee?: string;
  reference_number?: string;
  notes?: string;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
  status?: string;
  status_label?: string;
  documents?: DocumentRow[];
  cash_bank_entries?: LedgerRow[];
}

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "₹0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN");
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-IN");
  }
  return formatDateTime(value);
}

function isImage(path?: string): boolean {
  return !!path && /\.(png|jpe?g|gif|webp|bmp)$/i.test(path);
}

const ExpenseDetail: React.FC = () => {
  const { expenseId } = useParams<{ expenseId: string }>();
  const navigate = useNavigate();
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const expenseAccess =
    (accessList.find((a: AccessItem) => a.code === "expense_management") as AccessItem) ||
    ({} as AccessItem);
  const canWrite = !!expenseAccess.write;

  const [loading, setLoading] = useState(true);
  const [expense, setExpense] = useState<ExpenseDetailData | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [meta, setMeta] = useState<any>({
    categories: [],
    payment_methods: [],
    paid_from_options: [],
    accounts: [],
  });
  const [vendors, setVendors] = useState<any[]>([]);

  const load = () => {
    if (!expenseId) return;
    setLoading(true);
    fetchExpense(expenseId)
      .then((res: any) => setExpense(res?.data || null))
      .catch(() => setExpense(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseId]);

  useEffect(() => {
    fetchExpenseMeta()
      .then((res: any) => setMeta(res?.data || {}))
      .catch(() => undefined);
    WebService.getAPI({ action: "/v1/door-management/vendors?limit=100&page_number=1" })
      .then((res: any) => setVendors(res?.data?.vendors || []))
      .catch(() => setVendors([]));
  }, []);

  const handleCancel = () => {
    if (!expense) return;
    Swal.fire({
      title: "Cancel this expense?",
      text: "A reversal will be posted to the cash/bank account. The original amount stays unchanged.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c0392b",
      confirmButtonText: "Yes, cancel it",
    }).then((result) => {
      if (!result.isConfirmed) return;
      cancelExpense(expense.expense_id).then(() => {
        Swal.fire({ icon: "success", title: "Expense cancelled", timer: 1400, showConfirmButton: false });
        load();
      });
    });
  };

  if (loading) {
    return <div className={styles.loader}>Loading expense...</div>;
  }
  if (!expense) {
    return <div className={styles.empty}>Expense not found.</div>;
  }

  const posted = expense.status === "POSTED";

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.backRow} onClick={() => navigate("/expenses")}>
        ← Back to Expenses
      </div>
      <div className={styles.headerTopRow}>
        <div>
          <div className={styles.headerTitle}>{expense.expense_code || `EXP-${expense.expense_id}`}</div>
          <span className={`${styles.statusBadge} ${styles[expense.status || "POSTED"]}`}>
            {expense.status_label || expense.status}
          </span>
        </div>
        <div className={styles.headerActions}>
          {canWrite && posted && (
            <button type="button" className={styles.editBtn} onClick={() => setShowEdit(true)}>
              Edit
            </button>
          )}
          {canWrite && posted && (
            <button type="button" className={styles.cancelBtn} onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.infoGrid}>
          <div>
            <span className={styles.infoLabel}>Expense ID</span>
            <span className={styles.infoValue}>{expense.expense_code || expense.expense_id}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Date</span>
            <span className={styles.infoValue}>{formatDate(expense.expense_date)}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Category</span>
            <span className={styles.infoValue}>{expense.category_label || expense.category}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Amount</span>
            <span className={styles.infoValue}>{formatMoney(expense.amount)}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Payment method</span>
            <span className={styles.infoValue}>{expense.payment_method_label || expense.payment_method}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Cash/Bank account</span>
            <span className={styles.infoValue}>
              {expense.cash_bank_account_name || "—"} ({expense.paid_from_label || expense.paid_from})
            </span>
          </div>
          <div>
            <span className={styles.infoLabel}>Vendor / Payee</span>
            <span className={styles.infoValue}>{expense.vendor_payee || "—"}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Reference number</span>
            <span className={styles.infoValue}>{expense.reference_number || "—"}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Created by</span>
            <span className={styles.infoValue}>{expense.created_by_name || expense.created_by || "—"}</span>
          </div>
          <div>
            <span className={styles.infoLabel}>Created date/time</span>
            <span className={styles.infoValue}>{formatDateTime(expense.created_at)}</span>
          </div>
        </div>
        <div style={{ marginTop: 18 }}>
          <span className={styles.infoLabel}>Description</span>
          <span className={styles.infoValue}>{expense.description}</span>
        </div>
        {expense.notes && (
          <div style={{ marginTop: 14 }}>
            <span className={styles.infoLabel}>Notes</span>
            <div className={styles.notes}>{expense.notes}</div>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.headerTitle}>Supporting documents</div>
        {(expense.documents || []).length ? (
          <div className={styles.docsList}>
            {expense.documents?.map((doc) => {
              const href = mediaUrl(doc.file_name);
              return isImage(doc.file_name) ? (
                <a key={doc.document_id} href={href} target="_blank" rel="noreferrer">
                  <img src={href} alt={doc.original_name || "document"} className={styles.docThumb} />
                </a>
              ) : (
                <a key={doc.document_id} className={styles.docLink} href={href} target="_blank" rel="noreferrer">
                  {doc.original_name || "Document"}
                </a>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty}>No documents attached.</div>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.headerTitle}>Cash / Bank ledger</div>
        {(expense.cash_bank_entries || []).length ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Account</th>
                <th>Type</th>
                <th>Entry</th>
                <th>Amount</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {expense.cash_bank_entries?.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.entry_date)}</td>
                  <td>{entry.account_name}</td>
                  <td>{entry.related_type}</td>
                  <td>{entry.entry_type}</td>
                  <td>{formatMoney(entry.amount)}</td>
                  <td>{entry.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className={styles.empty}>No cash/bank entries.</div>
        )}
      </div>

      {showEdit && (
        <AddExpensePanel
          open={showEdit}
          mode="edit"
          editData={expense}
          categories={meta.categories || []}
          paymentMethods={meta.payment_methods || []}
          paidFromOptions={meta.paid_from_options || []}
          accounts={meta.accounts || []}
          vendors={vendors}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default ExpenseDetail;
